import { IScentworkFault, IScentworkScore, IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

const getFaultsSum = (faults: IScentworkFault[]): number => {
  return faults.length
    ? faults.map((f) => f.fault_count).reduce((c, d) => c + d)
    : 0;
};

export const getAkcScentworkFaults = (score: IScore): number | string => {
  if (score.dog_class?.level === Constants.dog_class_element_level.master) {
    return (
      score.scentwork_master?.reduce(
        (total, sm) => total + getFaultsSum(sm.faults ?? []),
        0
      ) ?? 0
    );
  }

  return getFaultsSum(score.scentwork?.faults ?? []);
};

export const getAkcScentworkPlacement = (
  score: IScore,
  eligibleScores: IScore[]
): { place: string; num_dogs: number } => {
  const isMaster =
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_master ||
    score.dog_class?.level === Constants.dog_class_element_level.master;
  const isExcellent =
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_excellent ||
    score.dog_class?.level === Constants.dog_class_element_level.excellent;
  const isInterior =
    score.dog_class?.show_element ===
    Constants.dog_class_element.scentwork_interior;

  // Determine if this score is qualified
  let isQualified = false;
  if (
    (isMaster || (isExcellent && isInterior)) &&
    score.scentwork_master &&
    Array.isArray(score.scentwork_master)
  ) {
    isQualified = score.scentwork_master.every(
      (search) => search.result_type === Constants.score_result_type.qualified
    );
  } else if (score.scentwork) {
    isQualified =
      score.scentwork.result_type === Constants.score_result_type.qualified;
  }

  if (!isQualified) {
    const resultType =
      (isMaster || (isExcellent && isInterior)) &&
      score.scentwork_master &&
      score.scentwork_master.length > 0
        ? score.scentwork_master.find(
            (s) => s.result_type !== Constants.score_result_type.qualified
          )?.result_type || Constants.score_result_type.not_qualified
        : score.scentwork?.result_type || "";

    let place = "";
    switch (resultType) {
      case Constants.score_result_type.not_qualified:
        place = "NQ";
        break;
      case Constants.score_result_type.disqualified:
        place = "DQ";
        break;
      case Constants.score_result_type.excused:
        place = "EX";
        break;
      case Constants.score_result_type.absent:
        place = "AB";
        break;
      default:
        place = "NQ";
    }
    return {
      place,
      num_dogs: eligibleScores.length,
    };
  }

  const sameClassScores = eligibleScores.filter(
    (s) =>
      s.dog_class?.show_element === score.dog_class?.show_element &&
      s.dog_class?.level === score.dog_class?.level &&
      s.dog_class?.section === score.dog_class?.section
  );

  const qualifiedScores = sameClassScores
    .filter((s) => {
      const isScoreMaster =
        s.dog_class?.level ===
        Constants.dog_class_element_level.scentwork_master;
      if (
        isScoreMaster &&
        s.scentwork_master &&
        Array.isArray(s.scentwork_master)
      ) {
        return s.scentwork_master.every(
          (search) =>
            search.result_type === Constants.score_result_type.qualified
        );
      }
      return (
        s.scentwork?.result_type === Constants.score_result_type.qualified
      );
    })
    .sort((a, b) => {
      const isMasterA =
        a.dog_class?.level ===
        Constants.dog_class_element_level.scentwork_master;
      const isMasterB =
        b.dog_class?.level ===
        Constants.dog_class_element_level.scentwork_master;

      let faultsA = 0;
      let timeA = "99:99:99";
      if (isMasterA && a.scentwork_master && Array.isArray(a.scentwork_master)) {
        faultsA = a.scentwork_master.reduce(
          (sum, search) => sum + getFaultsSum(search.faults ?? []),
          0
        );
        timeA = a.scentwork_master[0]?.search_time || "99:99:99";
      } else if (a.scentwork) {
        faultsA = getFaultsSum(a.scentwork.faults ?? []);
        timeA = a.scentwork.search_time || "99:99:99";
      }

      let faultsB = 0;
      let timeB = "99:99:99";
      if (isMasterB && b.scentwork_master && Array.isArray(b.scentwork_master)) {
        faultsB = b.scentwork_master.reduce(
          (sum, search) => sum + getFaultsSum(search.faults ?? []),
          0
        );
        timeB = b.scentwork_master[0]?.search_time || "99:99:99";
      } else if (b.scentwork) {
        faultsB = getFaultsSum(b.scentwork.faults ?? []);
        timeB = b.scentwork.search_time || "99:99:99";
      }

      const isCleanRunA = faultsA === 0;
      const isCleanRunB = faultsB === 0;

      if (isCleanRunA && isCleanRunB) {
        return timeA.localeCompare(timeB);
      }
      if (isCleanRunA && !isCleanRunB) {
        return -1;
      }
      if (!isCleanRunA && isCleanRunB) {
        return 1;
      }
      if (timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }
      return faultsA - faultsB;
    });

  const placement = qualifiedScores.findIndex(
    (s) => s.arm_number === score.arm_number
  );

  return {
    place: placement >= 0 ? (placement + 1).toString() : "1",
    num_dogs: eligibleScores.length,
  };
};

export const getAkcScentworkResult = (score: IScore): string => {
  const isMaster =
    score.dog_class?.level === Constants.dog_class_element_level.master ||
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_master;
  const isExcellent =
    score.dog_class?.level === Constants.dog_class_element_level.excellent ||
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_excellent;
  const isInterior =
    score.dog_class?.show_element ===
    Constants.dog_class_element.scentwork_interior;

  if (isMaster || (isExcellent && isInterior)) {
    if (score.scentwork_master && score.scentwork_master.length > 0) {
      const allQualified = score.scentwork_master.every(
        (search) => search.result_type === Constants.score_result_type.qualified
      );
      if (allQualified) {
        return Constants.score_result_type.qualified;
      }
    }
    return score.status === Constants.score_status.complete
      ? Constants.score_result_type.not_qualified
      : "No Result";
  } else {
    return score.status === Constants.score_status.complete
      ? score.scentwork?.result_type ?? Constants.score_result_type.not_qualified
      : "No Result";
  }
};

const getMasterLevelValue = (
  score: IScore,
  field: keyof IScentworkScore
): string => {
  let value = "";

  score.scentwork_master?.forEach((sm, i) => {
    if (sm) {
      const _value = sm[field];

      if (typeof _value === "string" || typeof _value === "number") {
        if (i === 1) {
          value = value + " / ";
        }
        value = value + _value;
      }
    }
  });

  return value;
};

export const getAkcScentworkFieldValue = (
  score: IScore,
  field: keyof IScentworkScore
): any => {
  const isMaster =
    score.dog_class?.level === Constants.dog_class_element_level.master ||
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_master;
  const isExcellent =
    score.dog_class?.level === Constants.dog_class_element_level.excellent ||
    score.dog_class?.level ===
      Constants.dog_class_element_level.scentwork_excellent;
  const isInterior =
    score.dog_class?.show_element ===
    Constants.dog_class_element.scentwork_interior;

  if (isMaster || (isExcellent && isInterior)) {
    return getMasterLevelValue(score, field);
  } else {
    return score.scentwork?.[field] ?? "";
  }
};
